import { NgClass } from '@angular/common'
import {
    Component,
    inject,
    signal,
    computed,
    input,
    ChangeDetectionStrategy,
    OnInit,
    OnDestroy,
} from '@angular/core'
import { Router, RouterModule } from '@angular/router'
import { AuthStateService } from '@mattrika/benefit-core'
import { NgIcon } from '@ng-icons/core'
import { HlmButtonImports } from '@spartan-ng/helm/button'
import { HeaderProfileMenuComponent } from '../header-profile-menu/header-profile-menu.component'

interface SubCategory {
    name: string
    icon: string
}

interface Category {
    name: string
    icon: string
    slug: string
    subcategories: SubCategory[]
}

@Component({
    selector: 'app-header-client',
    imports: [
        NgIcon,
        RouterModule,
        NgClass,
        ...HlmButtonImports,
        HeaderProfileMenuComponent,
    ],
    templateUrl: './header-client.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './header-client.component.css',
})
export class HeaderClientComponent implements OnInit, OnDestroy {
    pageTitle = input<string>('')
    userName = input<string>('')
    userRole = input<string>('')
    userEmail = input<string>('')
    isVisibleDropDown = signal<boolean>(false)
    hoveredItem = signal<string | null>(null)
    hoveredCategory = signal<string | null>(null)
    isMobileMenuOpen = signal<boolean>(false)
    isScrolled = signal<boolean>(false)
    private closeTimeout: ReturnType<typeof setTimeout> | null = null
    private scrollListener: (() => void) | null = null

    private router = inject(Router)
    readonly authState = inject(AuthStateService)

    readonly displayName = computed(
        () => this.userName() || this.authState.userNameSignal() || 'Client',
    )
    readonly displayRole = computed(
        () => this.userRole() || this.authState.userRoleSignal() || 'Client',
    )
    readonly displayEmail = computed(
        () => this.userEmail() || this.authState.userEmailSignal() || '',
    )
    readonly displayInitials = computed(() => {
        const name = this.userName()?.trim()
        if (name) {
            const parts = name.split(/\s+/)
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase()
            }
            if (name.length >= 2) {
                return name.slice(0, 2).toUpperCase()
            }
        }
        return this.authState.userInitialsSignal()
    })
    readonly isLoggedIn = computed(() => this.authState.isLoggedInSignal())
    readonly showClientDashboard = computed(
        () =>
            this.isLoggedIn() &&
            this.displayRole() === 'Client' &&
            !this.authState.isOwnerSignal(),
    )

    ngOnInit() {
        this.scrollListener = () => {
            this.isScrolled.set(window.scrollY > 20)
        }
        window.addEventListener('scroll', this.scrollListener, {
            passive: true,
        })
    }

    ngOnDestroy() {
        if (this.scrollListener) {
            window.removeEventListener('scroll', this.scrollListener)
        }
    }

    categories: Category[] = [
        {
            name: 'Groceries',
            icon: 'assets/images/categories/super-markets.png',
            slug: 'groceries',
            subcategories: [
                {
                    name: 'Fresh Produce',
                    icon: 'assets/images/categories/super-markets.png',
                },
                {
                    name: 'Dairy & Bakery',
                    icon: 'assets/images/categories/super-markets.png',
                },
                {
                    name: 'Snacks & Beverages',
                    icon: 'assets/images/categories/super-markets.png',
                },
            ],
        },
        {
            name: 'Electronics',
            icon: 'assets/images/categories/electronics.png',
            slug: 'electronics',
            subcategories: [
                {
                    name: 'Laptops & Computers',
                    icon: 'assets/images/categories/electronics.png',
                },
                {
                    name: 'Mobile Phones',
                    icon: 'assets/images/categories/electronics.png',
                },
                {
                    name: 'Home Appliances',
                    icon: 'assets/images/categories/electronics.png',
                },
            ],
        },
        {
            name: 'Fashion',
            icon: 'assets/images/categories/clothing-brands.png',
            slug: 'fashion',
            subcategories: [
                {
                    name: "Men's Wear",
                    icon: 'assets/images/categories/clothing-brands.png',
                },
                {
                    name: "Women's Wear",
                    icon: 'assets/images/categories/clothing-brands.png',
                },
                {
                    name: 'Accessories',
                    icon: 'assets/images/categories/clothing-brands.png',
                },
            ],
        },
        {
            name: 'Health & Fitness',
            icon: 'assets/images/categories/gym.png',
            slug: 'health-fitness',
            subcategories: [
                {
                    name: 'Gym Memberships',
                    icon: 'assets/images/categories/gym.png',
                },
                {
                    name: 'Supplements',
                    icon: 'assets/images/categories/gym.png',
                },
                {
                    name: 'Wellness & Spa',
                    icon: 'assets/images/categories/gym.png',
                },
            ],
        },
        {
            name: 'Dining & Travel',
            icon: 'assets/images/categories/restaurants.png',
            slug: 'dining-travel',
            subcategories: [
                {
                    name: 'Restaurants',
                    icon: 'assets/images/categories/restaurants.png',
                },
                {
                    name: 'Hotels & Resorts',
                    icon: 'assets/images/categories/hotels.png',
                },
                {
                    name: 'Flight Deals',
                    icon: 'assets/images/categories/flights.png',
                },
            ],
        },
        {
            name: 'Banking',
            icon: 'assets/images/categories/banking.png',
            slug: 'banking',
            subcategories: [
                {
                    name: 'Credit Cards',
                    icon: 'assets/images/categories/banking.png',
                },
                {
                    name: 'Loans & EMI',
                    icon: 'assets/images/categories/banking.png',
                },
                {
                    name: 'Savings Accounts',
                    icon: 'assets/images/categories/banking.png',
                },
            ],
        },
    ]

    openProfileDropDown() {
        this.isVisibleDropDown.set(!this.isVisibleDropDown())
    }

    navigateToClaimedItems() {
        this.router.navigate(['/client/claimed-items'])
    }

    logout() {
        this.authState.logout('/login')
    }

    get initials(): string {
        return this.displayInitials()
    }

    onHoverEnter(item: string) {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout)
            this.closeTimeout = null
        }
        this.hoveredItem.set(item)
    }

    onHoverLeave() {
        this.closeTimeout = setTimeout(() => {
            this.hoveredItem.set(null)
            this.hoveredCategory.set(null)
        }, 150)
    }

    onDrawerEnter() {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout)
            this.closeTimeout = null
        }
    }

    onCategoryEnter(categoryName: string) {
        this.hoveredCategory.set(categoryName)
    }

    onCategoryLeave() {
        this.hoveredCategory.set(null)
    }

    onDrawerLeave() {
        this.hoveredItem.set(null)
        this.hoveredCategory.set(null)
    }

    navigateToCategory(slug: string, event: Event) {
        event.preventDefault()
        event.stopPropagation()
        this.hoveredItem.set(null)
        this.hoveredCategory.set(null)
        this.router.navigate(['/category', slug])
    }

    toggleMobileMenu() {
        this.isMobileMenuOpen.update((v) => !v)
    }

    closeMobileMenu() {
        this.isMobileMenuOpen.set(false)
    }

    navigateToCategoryMobile(slug: string) {
        this.closeMobileMenu()
        this.router.navigate(['/category', slug])
    }

    navigateToClaimedItemsMobile() {
        this.closeMobileMenu()
        this.router.navigate(['/claimed-items'])
    }
}
